import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const SubsidyOverviewSection = () => {
  const [expandedSubsidy, setExpandedSubsidy] = useState(null);

  const subsidies = [
    {
      id: 'isde',
      name: 'ISDE - Investeringssubsidie Duurzame Energie',
      icon: 'Zap',
      status: 'available',
      statusText: 'Beschikbaar',
      amount: '€2.000 - €4.500',
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-50',
      description: 'Subsidie voor duurzame energieoplossingen zoals warmtepompen, zonneboilers en biomassketels.',
      eligibility: ['Particuliere woningeigenaren', 'VvE\'s', 'Huurders met toestemming verhuurder'],
      deadline: 'Aanvragen mogelijk tot budget op is',
      applications: [
        'Hybride warmtepomp: €2.000',
        'All-electric warmtepomp: €3.000',
        'Zonneboiler: €1.500',
        'Biomassaketel: €2.500'
      ]
    },
    {
      id: 'btw',
      name: 'BTW-teruggave zonnepanelen',
      icon: 'Sun',
      status: 'available',
      statusText: 'Beschikbaar',
      amount: '21% BTW terug',
      color: 'from-yellow-500 to-orange-500',
      bgColor: 'bg-yellow-50',
      description: 'Vraag de volledige BTW terug op de aanschaf en installatie van zonnepanelen.',
      eligibility: ['Particuliere woningeigenaren', 'Zonnepanelen op eigen woning', 'Maximaal 10.000 Wp'],
      deadline: 'Binnen 3 maanden na installatie',
      applications: [
        'Aanvraag via Belastingdienst',
        'Facturen en installatiebewijzen vereist',
        'Eenmalige teruggave mogelijk'
      ]
    },
    {
      id: 'seeh',
      name: 'SEEH - Subsidie Energiebesparing Eigen Huis',
      icon: 'Home',
      status: 'available',
      statusText: 'Beschikbaar',
      amount: '€750 - €3.000',
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-50',
      description: 'Subsidie voor isolatiemaatregelen zoals spouwmuur-, dak- en vloerisolatie.',
      eligibility: ['Woningeigenaren', 'Woning ouder dan 2 jaar', 'Erkende installateur verplicht'],
      deadline: 'Aanvragen mogelijk tot eind 2026',
      applications: [
        'Spouwmuurisolatie: €750',
        'Dakisolatie: €1.000',
        'Vloerisolatie: €1.200',
        'HR++ glas: €1.500'
      ]
    },
    {
      id: 'local',
      name: 'Lokale subsidies',
      icon: 'MapPin',
      status: 'check',
      statusText: 'Check uw gemeente',
      amount: 'Varieert per gemeente',
      color: 'from-purple-500 to-pink-500',
      bgColor: 'bg-purple-50',
      description: 'Veel gemeenten bieden extra subsidies bovenop landelijke regelingen.',
      eligibility: ['Inwoners van de gemeente', 'Voorwaarden verschillen per gemeente'],
      deadline: 'Varieert per gemeente',
      applications: [
        'Check website van uw gemeente',
        'Vaak extra subsidie mogelijk',
        'Combineerbaar met landelijke subsidies'
      ]
    },
    {
      id: 'tax',
      name: 'Fiscale voordelen',
      icon: 'Receipt',
      status: 'available',
      statusText: 'Beschikbaar',
      amount: 'Aftrekbaar van belasting',
      color: 'from-indigo-500 to-blue-500',
      bgColor: 'bg-indigo-50',
      description: 'Verduurzamingskosten kunnen onder voorwaarden fiscaal aftrekbaar zijn.',
      eligibility: ['Eigenaar-bewoners', 'Monumentenpanden', 'Energiebesparende maatregelen'],
      deadline: 'Bij jaarlijkse aangifte',
      applications: [
        'Aftrek bij monumentenpanden',
        'Energiebesparende voorzieningen',
        'Maximaal €3.200 per jaar aftrekbaar'
      ]
    },
    {
      id: 'future',
      name: 'Toekomstige regelingen',
      icon: 'Calendar',
      status: 'pending',
      statusText: 'Binnenkort beschikbaar',
      amount: 'Nog niet bekend',
      color: 'from-gray-500 to-gray-600',
      bgColor: 'bg-gray-50',
      description: 'Nieuwe subsidies worden regelmatig aangekondigd. Blijf op de hoogte!',
      eligibility: ['Nog niet bekend'],
      deadline: 'Nog niet bekend',
      applications: [
        'Thuisbatterij subsidie verwacht',
        'Uitbreiding ISDE mogelijk',
        'Nieuwe lokale regelingen'
      ]
    }
  ];

  const getStatusBadge = (status) => {
    const badges = {
      available: { color: 'bg-green-100 text-green-700', icon: 'CheckCircle' },
      pending: { color: 'bg-yellow-100 text-yellow-700', icon: 'Clock' },
      check: { color: 'bg-blue-100 text-blue-700', icon: 'Search' }
    };
    return badges?.[status];
  };

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-4">
            Beschikbare subsidies & regelingen
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Ontdek welke subsidies en fiscale voordelen u kunt benutten
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {subsidies?.map((subsidy, index) => {
            const badge = getStatusBadge(subsidy?.status);
            const isExpanded = expandedSubsidy === subsidy?.id;

            return (
              <motion.div
                key={subsidy?.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.02, y: -5 }}
                className={`${subsidy?.bgColor} rounded-2xl p-6 shadow-elevation-2 border border-gray-200 cursor-pointer`}
                onClick={() => setExpandedSubsidy(isExpanded ? null : subsidy?.id)}
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 bg-gradient-to-br ${subsidy?.color} rounded-xl flex items-center justify-center shadow-lg`}>
                    <Icon name={subsidy?.icon} size={24} className="text-white" />
                  </div>
                  <div className={`flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-semibold ${badge?.color}`}>
                    <Icon name={badge?.icon} size={14} />
                    <span>{subsidy?.statusText}</span>
                  </div>
                </div>
                {/* Title */}
                <h3 className="text-lg font-bold text-foreground mb-2">{subsidy?.name}</h3>
                {/* Amount */}
                <div className="flex items-center space-x-2 mb-4">
                  <Icon name="Euro" size={16} className="text-muted-foreground" />
                  <p className="text-xl font-bold text-primary">{subsidy?.amount}</p>
                </div>
                {/* Description */}
                <p className="text-sm text-muted-foreground mb-4">{subsidy?.description}</p>
                {/* Expandable Details */}
                <motion.div
                  initial={false}
                  animate={{ height: isExpanded ? 'auto' : 0 }}
                  className="overflow-hidden"
                >
                  <div className="pt-4 border-t border-gray-200 space-y-4">
                    {/* Eligibility */}
                    <div>
                      <p className="text-xs font-semibold text-foreground mb-2">Wie komt in aanmerking:</p>
                      <ul className="space-y-1">
                        {subsidy?.eligibility?.map((item, i) => (
                          <li key={i} className="flex items-start space-x-2 text-xs text-muted-foreground">
                            <Icon name="Check" size={12} className="text-success mt-0.5" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Applications */}
                    <div>
                      <p className="text-xs font-semibold text-foreground mb-2">Toepassingen:</p>
                      <ul className="space-y-1">
                        {subsidy?.applications?.map((item, i) => (
                          <li key={i} className="flex items-start space-x-2 text-xs text-muted-foreground">
                            <Icon name="ArrowRight" size={12} className="text-primary mt-0.5" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Deadline */}
                    <div className="flex items-center space-x-2 text-xs">
                      <Icon name="Calendar" size={12} className="text-muted-foreground" />
                      <span className="text-muted-foreground">{subsidy?.deadline}</span>
                    </div>
                  </div>
                </motion.div>
                {/* Expand Button */}
                <motion.div
                  className="flex items-center justify-center mt-4 text-primary text-sm font-semibold"
                  animate={{ rotate: isExpanded ? 180 : 0 }}
                >
                  <Icon name="ChevronDown" size={20} />
                </motion.div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default SubsidyOverviewSection;